import type { RouteRecordRaw } from 'vue-router'
import { useCreditNotesStore } from '@/stores/credit-notes'
import { useInvoicesStore } from '@/stores/invoices'
import { InvoiceStatus } from '@/enums/InvoiceStatus'

export const routes: RouteRecordRaw[] = [
  {
    path: '/finance/credit-notes',
    name: 'credit-notes',
    meta: {
      title: 'Credit Notes'
    },
    component: () => import('@/views/finance/credit-notes/CreditNoteListView.vue')
  },
  {
    path: '/finance/credit-notes/linguist/:linguistId',
    name: 'credit-notes-by-linguist',
    props: true,
    meta: {
      title: 'Credit Notes'
    },
    component: () => import('@/views/finance/credit-notes/CreditNoteListView.vue')
  },
  {
    path: '/finance/credit-notes/:id',
    name: 'credit-note-detail',
    props: true,
    meta: {
      title: 'Credit Note'
    },
    component: () => import('@/views/finance/credit-notes/CreditNoteDetailView.vue')
  },
  {
    path: '/finance/credit-notes/:id/edit',
    name: 'credit-note-edit',
    props: true,
    meta: {
      title: 'Edit Credit Note'
    },
    component: () => import('@/views/finance/credit-notes/CreditNoteEditView.vue'),
    // The CCN editor (issue #1829) is only reachable on cancelled CCNs.
    // The cancelled CCN is the immutable starting template for composing a
    // replacement; live CCNs have no business being edited. Verify state in
    // the store before mounting so a direct URL hit short-circuits to the
    // detail view.
    beforeEnter: async (to) => {
      const store = useCreditNotesStore()
      const id = to.params.id as string
      try {
        await store.fetchCcn(id)
      } catch {
        return { name: 'credit-notes' }
      }
      if (!store.currentCcn || !store.currentCcn.cancelled) {
        return { name: 'credit-note-detail', params: { id } }
      }
    }
  },
  {
    path: '/finance/invoices',
    name: 'invoices',
    meta: {
      title: 'Invoices'
    },
    component: () => import('@/views/finance/invoices/InvoiceListView.vue')
  },
  {
    // Declared before the `:id` detail route so the static `create` segment
    // isn't captured as an invoice id.
    path: '/finance/invoices/create',
    name: 'invoice-create',
    meta: {
      title: 'Create Invoice'
    },
    component: () => import('@/views/finance/invoices/InvoiceCreateView.vue')
  },
  {
    path: '/finance/invoices/:id',
    name: 'invoice-detail',
    props: true,
    meta: {
      title: 'Invoice'
    },
    component: () => import('@/views/finance/invoices/InvoiceDetailView.vue')
  },
  {
    path: '/finance/invoices/:id/edit',
    name: 'invoice-edit',
    props: true,
    meta: {
      title: 'Edit Invoice'
    },
    component: () => import('@/views/finance/invoices/InvoiceEditView.vue')
  },
  {
    path: '/finance/invoices/:id/editor',
    name: 'invoice-editor',
    props: true,
    meta: {
      title: 'Collective Invoice Editor'
    },
    component: () => import('@/views/finance/invoices/InvoiceEditorView.vue'),
    // #1966: the collective invoice editor composes a corrected replacement
    // from a cancelled collective (the CCN-editor analogue). Only reachable for
    // a cancelled (void) collective; verify state before mounting so a direct
    // URL hit short-circuits to the detail view.
    beforeEnter: async (to) => {
      const store = useInvoicesStore()
      const id = to.params.id as string
      try {
        await store.fetchInvoice(id)
      } catch {
        return { name: 'invoices' }
      }
      const invoice = store.currentInvoice
      if (
        !invoice ||
        !invoice.collectiveInvoice ||
        !invoice.cancelled ||
        invoice.status !== InvoiceStatus.VOID
      ) {
        return { name: 'invoice-detail', params: { id } }
      }
    }
  }
]

export default routes
